import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {endOfDay, endOfToday, endOfYesterday, isAfter, isBefore, isFuture, isPast, startOfDay, startOfToday, startOfTomorrow} from 'date-fns';
import {toLocalMidnight} from '@aviary/store/common/date.helpers';
import {DateOnlyIsoString} from '@aviary/store/common/common.types';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {isExtendedFormControl} from '@aviary/forms/form.helpers';

export namespace DateValidators { // eslint-disable-line @typescript-eslint/no-namespace -- Keeps these validators parallel to the standard Validators.* naming.
  /** Validates that the control's date is before today's date. */
  export function pastDate(control: AbstractControl): ValidationErrors | null {
    return isPast(endOfDay(toLocalMidnight(control.value))) ? null : {mustBePastDate: true};
  }

  /** Validates that the control's date is today's date or after. */
  export function notPastDate(control: AbstractControl): ValidationErrors | null {
    return isPast(endOfDay(toLocalMidnight(control.value))) ? {cannotBePastDate: true} : null;
  }

  /** Validates that the control's date is tomorrow's date or after. */
  export function futureDate(control: AbstractControl): ValidationErrors | null {
    return isFuture(toLocalMidnight(control.value)) ? null : {mustBeFutureDate: true};
  }

  /** Assumes pristine values to be valid, but otherwise wraps the futureDate validator. */
  export function pristineOrFutureDate(control: AbstractControl): ValidationErrors | null {
    // control.pristine doesn't seem to be trustworthy here, as if validation runs before pristine is updated
    return isExtendedFormControl(control) && control.value === control.initialValue ? null : futureDate(control)
  }

  /** Validates that the control's date is today's date or before. */
  export function notFutureDate(control: AbstractControl): ValidationErrors | null {
    return isFuture(startOfDay(toLocalMidnight(control.value))) ? {cannotBeFutureDate: true} : null;
  }

  export function optionalFutureDate(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    return futureDate(control);
  }

  export function notBeforeOld(control: AbstractControl): ValidationErrors | null {
    const defaultValue = (control as ExtendedFormControl).defaultValue;
    if (!control.value || !defaultValue) {
      return null;
    }
    return isBefore(toLocalMidnight(control.value), toLocalMidnight(defaultValue)) ? {cannotBeBeforeOldDate: true} : null;
  }

  export function notBefore(date: DateOnlyIsoString, dateDescription: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return isBefore(toLocalMidnight(control.value), toLocalMidnight(date))
        ? {cannotBeBeforeDate: {dateDescription}}
        : null;
    };
  }

  export function notAfter(date: DateOnlyIsoString, dateDescription: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return isAfter(toLocalMidnight(control.value), toLocalMidnight(date))
        ? {cannotBeAfterDate: {dateDescription}}
        : null;
    };
  }
}

export function calculateMinDateFromValidatorsFor(control: ExtendedFormControl): Date | undefined {
  if (control.hasValidator(DateValidators.futureDate) || control.hasValidator(DateValidators.optionalFutureDate) || control.hasValidator(DateValidators.pristineOrFutureDate)) return startOfTomorrow();
  if (control.hasValidator(DateValidators.notPastDate)) return startOfToday();

  return undefined;
}

export function calculateMaxDateFromValidatorsFor(control: ExtendedFormControl): Date | undefined {
  if (control.hasValidator(DateValidators.pastDate)) return endOfYesterday();
  if (control.hasValidator(DateValidators.notFutureDate)) return endOfToday();

  return undefined;
}
